import { useLocation, useNavigate, useParams } from "react-router-dom";
import { cn } from "@lib/utils/helpers";
import { APPLICANT_CONTENT } from '@/features/applicant/constants/applicantContent'
import {
  User,
  Home,
  Briefcase,
  FileText,
  PenTool,
  CreditCard,
} from "lucide-react";
import { Fragment } from "react";
import { useTabValidation } from "@/features/applicant/context/TabValidationContext";
import { useAppSelector } from "@/app/hooks";
import {
  selectApplicationData,
  selectIsReadOnly,
} from "@/features/applicant/applicationSlice";
import {
  APPLICATION_TABS,
  getTabPathWithApplicantId,
  getTabNavigationOptions,
  getCurrentTabFromLocation,
  canAccessTab,
} from "@lib/utils/applicationNavigation";
import { showInfo, showWarning } from "@lib/utils/notifications";

interface Step {
  name: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  shortName: string;
}

export function ApplicationProgressStepper() {
  const location = useLocation();
  const navigate = useNavigate();
  const { applicantId } = useParams<{ applicantId?: string }>();
  const { validate, saveCurrent } = useTabValidation();
  const applicationData = useAppSelector(selectApplicationData);
  const isReadOnly = useAppSelector(selectIsReadOnly);

  // Check if we're on the new route format (/resident-application/:applicantId)
  const isNewRouteFormat = location.pathname.match(
    /^\/resident-application\/\d+$/,
  );

  // Get current tab from location state (URL has no ?tab= query)
  const currentTab = getCurrentTabFromLocation(location);

  const steps: Step[] = APPLICATION_TABS.map((tab, index) => {
    // Use new route format if applicantId is available, otherwise use old format
    const path =
      applicantId && isNewRouteFormat
        ? getTabPathWithApplicantId(tab, applicantId)
        : `/applicant/application/${tab}`;

    const icons = [User, Home, Briefcase, FileText, PenTool, CreditCard];
    const names = [
      APPLICANT_CONTENT.application.steps.about.name,
      APPLICANT_CONTENT.application.steps.property.name,
      APPLICANT_CONTENT.application.steps.income.name,
      APPLICANT_CONTENT.application.steps.history.name,
      APPLICANT_CONTENT.application.steps.signature.name,
      APPLICANT_CONTENT.application.steps.payment.name,
    ];
    const shortNames = [
      APPLICANT_CONTENT.application.steps.about.name,
      APPLICANT_CONTENT.application.steps.property.shortName,
      APPLICANT_CONTENT.application.steps.income.name,
      APPLICANT_CONTENT.application.steps.history.shortName,
      APPLICANT_CONTENT.application.steps.signature.name,
      APPLICANT_CONTENT.application.steps.payment.name,
    ];

    return {
      name: names[index],
      path,
      icon: icons[index],
      shortName: shortNames[index],
    };
  });

  // Help / About lives outside the application wizard; do not highlight a step there.
  const isApplicantHelpRoute = location.pathname === "/applicant/help";

  // Determine active index based on current route format
  // The stepper must show the same tab that ResidentApplication is rendering
  // ResidentApplication uses getCurrentTabFromLocation which defaults to 'about'
  let activeIndex = 0;

  if (isApplicantHelpRoute) {
    activeIndex = -1;
  } else if (isNewRouteFormat && applicantId) {
    // Use the same logic as ResidentApplication: currentTab from location state, default to 'about'
    // This ensures the stepper highlights the tab that's actually being displayed
    activeIndex = APPLICATION_TABS.indexOf(currentTab);
    if (activeIndex === -1) activeIndex = 0;
  } else {
    // Use old format - find by pathname
    const currentStepIndex = steps.findIndex(
      (step) => step.path === location.pathname,
    );
    activeIndex = currentStepIndex >= 0 ? currentStepIndex : 0;
  }

  // Handle tab click: validate (forward) -> navigate -> auto-save the left tab in the background
  const handleTabClick = (
    e: React.MouseEvent,
    targetIndex: number,
    targetPath: string,
  ) => {
    e.preventDefault();

    // If clicking current wizard step, do nothing (activeIndex -1 = Help / About: never matches)
    if (activeIndex >= 0 && targetIndex === activeIndex) {
      return;
    }

    // Only validate when moving forward and not in read-only mode.
    // In read-only mode (e.g. after application fee paid), allow free navigation via stepper.
    // From Help / About (activeIndex -1), skip forward validation so users can return to any allowed tab.
    const isGoingBack = activeIndex >= 0 && targetIndex < activeIndex;
    const fromOutsideWizard = activeIndex < 0;
    const isEditableForwardMove = !isReadOnly && !isGoingBack && !fromOutsideWizard;
    if (isEditableForwardMove) {
      const validation = validate();
      if (!validation.isValid) {
        return; // Navigation blocked by validate() which also shows the modal
      }
    }

    const targetTab = APPLICATION_TABS[targetIndex];

    // STRICT STEP-WISE NAVIGATION: Check if target tab can be accessed (read-only allows any tab)
    const accessCheck = canAccessTab(
      targetIndex,
      applicationData,
      isReadOnly,
    );

    if (!accessCheck.canAccess) {
      showInfo(accessCheck.reason || "Please complete previous steps.", 0);
      return;
    }

    // Navigate FIRST so the tab switches immediately.
    // Use location state for new route format, regular path for old format.
    if (isNewRouteFormat && applicantId) {
      navigate(targetPath, getTabNavigationOptions(targetTab));
    } else {
      navigate(targetPath);
    }

    // THEN auto-save the tab we just left, in the BACKGROUND (not awaited) — the tab
    // change is never blocked by the save. Mirrors Angular tabClick, which fires
    // submitForm('fromtab') and navigates without waiting for the POST response.
    // saveCurrent() runs synchronously up to the dispatch, so the leaving tab's saver
    // is still registered (it unmounts only after this synchronous handler returns),
    // and each saver self-guards on dirty state (no PUT when nothing changed).
    if (isEditableForwardMove) {
      void saveCurrent().catch(() => {
        showWarning("Failed to save your changes. Please try again.");
      });
    }
  };

  return (
    <>
      <div
        data-testid="applicant-application-stepper"
        className="mx-3 sm:mx-6 mt-4 sm:mt-6 mb-4 bg-white rounded-xl shadow-md border border-gray-200"
      >
        <div className="px-3 sm:px-6 py-3 sm:py-4 overflow-x-auto scrollbar-thin touch-scroll">
          {/* Single flex row: step, connector, step, connector, ... - horizontal scroll on small screens */}
          <div className="flex items-center w-full min-w-max">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = index === activeIndex;
              // Past step: strictly index < activeIndex
              // We removed "completion" logic, so we just show history
              const isPastStep = index < activeIndex;
              const isUpcoming = index > activeIndex;

              return (
                <Fragment key={APPLICATION_TABS[index]}>
                  {/* Step circle and label */}
                  <div
                    data-testid={`applicant-application-step-${index}`}
                    onClick={(e) => {
                      handleTabClick(e, index, step.path);
                    }}
                    className={cn(
                      "relative flex flex-col items-center flex-shrink-0 group transition-all cursor-pointer",
                    )}
                    title={step.name}
                  >
                    <div
                      className={cn(
                        "relative flex items-center justify-center w-8 h-8 rounded-full border-2 transition-all duration-300",
                        // Active: blue border with light blue background
                        isActive &&
                        "border-[#1597ff] bg-[#1597ff]/10 shadow-md shadow-[#1597ff]/20",
                        // Past step: solid blue background (was "Completed")
                        isPastStep &&
                        "border-[#1597ff] bg-[#1597ff] text-white",
                        // Upcoming: gray border
                        isUpcoming &&
                        "border-gray-300 bg-white text-gray-400",
                      )}
                    >
                      {/* Always show the step icon, never a checkmark */}
                      <Icon
                        className={cn(
                          "w-3.5 h-3.5 transition-colors",
                          isActive && "text-[#1597ff]",
                          isPastStep && "text-white",
                          isUpcoming && "text-gray-400",
                        )}
                      />
                    </div>
                    <div className="mt-1">
                      <p
                        className={cn(
                          "text-[10px] font-medium transition-colors text-center whitespace-nowrap",
                          isActive && "text-[#1597ff] font-semibold",
                          isPastStep && "text-[#1597ff]",
                          isUpcoming && "text-gray-500",
                        )}
                      >
                        {step.shortName}
                      </p>
                    </div>
                  </div>

                  {/* Connector line - blue if step is past */}
                  {index < steps.length - 1 && (
                    <div
                      className={cn(
                        "flex-1 h-0.5 min-w-[12px] mx-1 transition-all duration-300 self-center",
                        // Line is blue if the CURRENT step is past (meaning we are connecting FROM a past step)
                        // This makes the line leading UP TO the active step blue
                        index < activeIndex ? "bg-[#1597ff]" : "bg-gray-200",
                      )}
                      aria-hidden
                    />
                  )}
                </Fragment>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
